import Div from 'components/Div';
import FormInput from 'components/FormInput/index';
import ElectricityIcon from '@enerex/unified-ui-react/icons/ElectricityIcon';
import GasIcon from '@enerex/unified-ui-react/icons/GasIcon';
import CloseIcon from '@enerex/unified-ui-react/icons/CloseIcon';
import ConfirmationModal from '@enerex/unified-ui-react/ConfirmationModal';
import { CustomPricingState } from '../../store/state';
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Form, Button, Badge, Dropdown } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedMarket } from '../../store/actions';
import { useWatch,useFormContext  } from 'react-hook-form';
import '../../customPricing.scss';

interface FilterItem {
  title: string;
  key: string;
  isOpen: boolean;
}

type DropdownLoadingMap = {
  states?: boolean;
  utilities?: boolean;
  rateClasses?: boolean;
  loadProfiles?: boolean;
  loadZones?: boolean;
  billingMethods?: boolean;
  adminClients?: boolean;
  costComponents?: boolean;
  customerTypes?: boolean;
};

const items: FilterItem[] = [
  { title: 'Market', key: 'Market', isOpen: true },
  { title: 'Geography', key: 'Geography', isOpen: false },
  { title: 'Load Characteristics', key: 'LoadCharacteristics', isOpen: false },
  { title: 'Pricing Parameters', key: 'PricingParameters', isOpen: false },
];

const EXCLUSIVE_PASS_THROUGH_VALUES = new Set(["All", "All Inclusive"]);
const NO_OF_MONTH_OPTIONS = Array.from({ length: 24 }, (_, index) => {
  const value = String(index + 1);
  return { value, name: value };
});

const AccordionFilters = ({ hide, handleSubmit, register, errors, control, reset, dependantDropdowns, onDeleteFilter, resetKey, adminClients, trigger, dropdownLoading, setValue, filterActionsDisabled, savedFilters, onSaveFilters, onDeleteSavedFilter, onApplySavedFilter, savingFilters, selectedSavedFilterId }: { hide?: () => void; handleSubmit: any; register?: any; errors?: any; control?: any; reset?: any; dependantDropdowns: any; onDeleteFilter?: any; resetKey?: number; adminClients?: any[]; trigger?: any; dropdownLoading?: DropdownLoadingMap; setValue?: any; filterActionsDisabled?: boolean; savedFilters?: Array<{ id: string; name: string }>; onSaveFilters?: (name: string) => void; onDeleteSavedFilter?: (filterId: string) => void; onApplySavedFilter?: (filterId: string) => void; savingFilters?: boolean; selectedSavedFilterId?: string | null; }) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [filterName, setFilterName] = useState("");

  const pricingState = useSelector((state: { customPricing: CustomPricingState }) => state.customPricing);
  const termsList = pricingState.terms;
  const markets = pricingState.markets;
  const selectedMarket = pricingState.selectedMarket;
  const productTypeList = pricingState.productTypes;
  const costComponentList = pricingState.costComponents;
  const baseCostComponents = useMemo(() => {
    const filtered =
      costComponentList?.filter((item: any) => item.value !== "All") || [];
    const allInclusiveOption = filtered.find(
      (item: any) => item.value === "All Inclusive"
    );
    const remainingOptions = filtered
      .filter((item: any) => item.value !== "All Inclusive")
      .sort((a: any, b: any) =>
        String(a?.name || a?.value || "").localeCompare(
          String(b?.name || b?.value || ""),
          undefined,
          { sensitivity: "base" }
        )
      );

    return allInclusiveOption
      ? [allInclusiveOption, ...remainingOptions]
      : remainingOptions;
  }, [costComponentList]);
  const dispatch = useDispatch();
  const dropdownLoadingState: DropdownLoadingMap = dropdownLoading || {};


  // Watch form fields for cross-field validation
  const watchUsageFrom = useWatch({ control, name: 'usageFrom' });
  const watchUsageTo = useWatch({ control, name: 'usageTo' });
  const watchLoadFactorFrom = useWatch({ control, name: 'loadFactorFrom' });
  const watchLoadFactorTo = useWatch({ control, name: 'loadFactorTo' });
  const watchedPassThrough = useWatch({ control, name: 'passThrough' });
  const watchedStartDate = useWatch({ control, name: 'startToDate' });
  const watchedEffectiveDate = useWatch({ control, name: 'effectiveDate' });
  const watchedNoOfMonth = useWatch({ control, name: 'noOfMonth' });
  const { exclusivePassThroughValue, hasNonExclusiveSelection } = useMemo(() => {
    const selections = Array.isArray(watchedPassThrough)
      ? watchedPassThrough
      : watchedPassThrough
      ? [watchedPassThrough]
      : [];
    const exclusiveSelection = selections.find((item: any) => {
      const value = item?.value ?? item;
      return (
        typeof value === "string" && EXCLUSIVE_PASS_THROUGH_VALUES.has(value)
      );
    });
    const exclusiveValue =
      exclusiveSelection?.value ?? exclusiveSelection ?? null;
    const nonExclusiveSelected = selections.some((item: any) => {
      const value = item?.value ?? item;
      return (
        value &&
        typeof value === "string" &&
        !EXCLUSIVE_PASS_THROUGH_VALUES.has(value)
      );
    });
    return {
      exclusivePassThroughValue: exclusiveValue,
      hasNonExclusiveSelection: nonExclusiveSelected,
    };
  }, [watchedPassThrough]);

  // When "All" is selected as Pass Through default, hide it from the options list
  // When other options are selected, "All" should remain available in options
  const passThroughOptions = useMemo(() => {
    const selections = Array.isArray(watchedPassThrough)
      ? watchedPassThrough
      : watchedPassThrough
      ? [watchedPassThrough]
      : [];
    const isAllSelected = selections.some((item: any) => {
      const value = item?.value ?? item;
      return value === "All";
    });

    // If "All" is selected, hide it from options
    if (isAllSelected) {
      return baseCostComponents.filter((item: any) => item.value !== "All");
    }

    // If "All" is not selected, add it back to the options list
    const allOption = costComponentList?.find((item: any) => item.value === "All");
    if (allOption) {
      return [allOption, ...baseCostComponents];
    }

    return baseCostComponents;
  }, [baseCostComponents, watchedPassThrough, costComponentList]);

  // Auto-filter passThrough selections based on exclusive rules
  useEffect(() => {
    if (!setValue || !watchedPassThrough) return;
    
    const selections = Array.isArray(watchedPassThrough)
      ? watchedPassThrough
      : watchedPassThrough
      ? [watchedPassThrough]
      : [];
    
    if (selections.length === 0) return;

    // Extract values from selections
    const getValue = (item: any) => item?.value ?? item;
    const values = selections.map(getValue);

    // Check what's selected
    const hasAll = values.includes("All");
    const hasAllInclusive = values.includes("All Inclusive");
    const hasExclusive = hasAll || hasAllInclusive;
    const hasNonExclusive = values.some((v: any) => v && typeof v === "string" && !EXCLUSIVE_PASS_THROUGH_VALUES.has(v));

    let filteredSelections: any[] = [];

    // Rule 3: If both "All" and "All Inclusive" are selected, keep only the last one
    if (hasAll && hasAllInclusive) {
      // Keep only the last selected exclusive value (the one that appears last in the array)
      const lastExclusive = selections.filter((item: any) => {
        const value = getValue(item);
        return EXCLUSIVE_PASS_THROUGH_VALUES.has(value);
      }).pop();
      filteredSelections = lastExclusive ? [lastExclusive] : [];
    }
    // Rule 1 & 2: When both exclusive and non-exclusive are present, check the last selected item
    else if (hasNonExclusive && hasExclusive) {
      // Get the last selected item to determine which rule to apply
      const lastSelected = selections[selections.length - 1];
      const lastSelectedValue = getValue(lastSelected);
      const isLastExclusive = EXCLUSIVE_PASS_THROUGH_VALUES.has(lastSelectedValue);
      
      if (isLastExclusive) {
        // Rule 2: Last selected is exclusive, remove non-exclusive options
        filteredSelections = selections.filter((item: any) => {
          const value = getValue(item);
          return EXCLUSIVE_PASS_THROUGH_VALUES.has(value);
        });
      } else {
        // Rule 1: Last selected is non-exclusive, remove exclusive options
        filteredSelections = selections.filter((item: any) => {
          const value = getValue(item);
          return value && typeof value === "string" && !EXCLUSIVE_PASS_THROUGH_VALUES.has(value);
        });
      }
    }
    // No violations, keep selections as is
    else {
      filteredSelections = selections;
    }

    // Only update if filtered selection is different from current
    const currentValues = values.sort().join(',');
    const filteredValues = filteredSelections.map(getValue).sort().join(',');
    
    if (currentValues !== filteredValues) {
      // Use setTimeout to avoid conflict with Controller's onChange
      setTimeout(() => {
        setValue('passThrough', filteredSelections.length > 0 ? filteredSelections : [], { shouldDirty: true });
      }, 0);
    }
  }, [watchedPassThrough, setValue]);

  // Validation will only trigger on form submit (Apply button click)
  // Removed real-time validation triggers to show errors only on Apply

  // Selected values for badges
  const selectedState = pricingState.selectedState?.name;
  // Use Redux state for term badge (updates only on Apply), similar to Start Date
  const selectedTerm = pricingState.selectedTerm && pricingState.selectedTerm !== "" ? String(pricingState.selectedTerm) : null;
  const selectedUtility = pricingState?.selectedUtility?.name;
  const selectedRateClass = pricingState?.selectedRateClass?.name;
  const selectedLoadZone = pricingState?.selectedLoadZone?.name;
  const selectedBillingMethod = pricingState?.selectedBillingMethod?.name;
  const selectedCommodity = pricingState.commodity === 2 ? 'Nat Gas' : 'Electric';
  const usage = pricingState.usage; // legacy
  const usageFrom = pricingState.usageFrom;
  const usageTo = pricingState.usageTo;
  const demand = pricingState.demand;
  const loadFactor = pricingState.loadFactor; // legacy
  const loadFactorFrom = pricingState.loadFactorFrom;
  const loadFactorTo = pricingState.loadFactorTo;
  // const selectedProductType = pricingState?.productTypes.find((s: any) => s.isSelected)?.name;
  const selectedProductType = pricingState?.productTypes
  .filter((s: any) => s.isSelected)
  .map((s: any) => s.value)
  .join(', ') || '';
  const selectedCostComponent = pricingState?.costComponents
  .filter((s: any) => s.isSelected)
  .map((s: any) => s.value)
  .join(', ') || '';
  const selectedCustomerclass = pricingState.customerClass && pricingState.customerClass !== "" ? pricingState.customerClass : null;
  const selectedNoOfMonth =
    pricingState.noOfMonths ||
    watchedNoOfMonth?.value ||
    (watchedNoOfMonth?.name as string) ||
    '12';

  const toShortDate = (d?: string | Date | any | null) => {
    if (!d) return null;

    let dt: Date | null = null;

    if (d && typeof d === 'object' && typeof d.toDate === 'function') {
      dt = d.toDate();
    } else if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      // If it's a string in YYYY-MM-DD format, parse it as local date to avoid timezone shifts
      const [year, month, day] = d.split('-').map(Number);
      dt = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      dt = new Date(d);
    }

    if (!dt || isNaN(dt.getTime())) return null;

    // Format as mm/dd/yy
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    const yy = String(dt.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  const toMonthYearDate = (d?: string | Date | any | null) => {
    if (!d) return null;

    let dt: Date | null = null;

    if (d && typeof d === 'object' && typeof d.toDate === 'function') {
      dt = d.toDate();
    } else if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      // If it's a string in YYYY-MM-DD format, parse it as local date to avoid timezone shifts
      const [year, month, day] = d.split('-').map(Number);
      dt = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      dt = new Date(d);
    }

    if (!dt || isNaN(dt.getTime())) return null;

    // Format as mm/yy
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yy = String(dt.getFullYear()).slice(-2);
    return `${mm}/${yy}`;
  };

  // Check if current market is gas (handle various commodity naming conventions)
  const commodityStr = selectedMarket?.commodity ? String(selectedMarket.commodity).toLowerCase() : '';
  const isGasMarket = commodityStr.includes('gas') || commodityStr.includes('natgas');
  const usageLabel = isGasMarket ? "Usage (therm)" : "Usage (kWh)";

  // Only use values from Redux (pricingState) so that badges update
  // when user clicks Apply, not on every onChange in the form.
  const startDateFallback =
    pricingState.startToDate || pricingState.startFromDate;
  const formattedStartDate = startDateFallback
    ? toMonthYearDate(startDateFallback)
    : null;

  const formatEffectiveBadge = () => {
    // Only use values from Redux (pricingState) so that badges update
    // when user clicks Apply, not on every onChange in the form.
    if (pricingState.effectiveFromDate && pricingState.effectiveToDate) {
      return `${toShortDate(
        pricingState.effectiveFromDate
      )} - ${toShortDate(pricingState.effectiveToDate)}`;
    }
    if (pricingState.effectiveToDate) {
      return toShortDate(pricingState.effectiveToDate);
    }
    if (pricingState.effectiveFromDate) {
      return toShortDate(pricingState.effectiveFromDate);
    }
    return null;
  };

  const effectiveBadgeValue = formatEffectiveBadge();

  const selectedFilters: Record<string, string | null> = {
    Market: selectedMarket?.name || 'All',
    State: selectedState || 'All',
    Utility: selectedUtility || 'All',
    'Load Zone': !isGasMarket ? (selectedLoadZone || 'All') : null,
    'Rate Class': pricingState.selectedState?.name === 'Texas' ? (pricingState.selectedLoadProfile?.name || 'All') : (selectedRateClass || 'All'),
    'Billing Method': selectedBillingMethod || 'All',
    'Pass Through': selectedCostComponent || '',
    Term: (pricingState.selectedTerm && pricingState.selectedTerm !== '') ? String(pricingState.selectedTerm) : null,
    ProductType: selectedProductType || '',
    'Effective Date': effectiveBadgeValue || 'All',
    'Start Date': formattedStartDate || 'All',
    'No Of Month': selectedNoOfMonth || 'All',
    'Customer Class': selectedCustomerclass || 'All',
  };

  const getBadgesForSection = (sectionKey: string) => {
    // Helper to format usage range, only showing if meaningful values exist
    const formatUsageRange = () => {
      const hasFrom = usageFrom !== null && usageFrom !== undefined;
      const hasTo = usageTo !== null && usageTo !== undefined;
      
      if (!hasFrom && !hasTo) {
        return null;
      }
      
      // Build range string - show "From - To" format
      if (hasFrom && hasTo) {
        return `${usageFrom} - ${usageTo}`;
      }
      
      // If only one is provided, still show it (validation will handle requiring both)
      const fromStr = hasFrom ? String(usageFrom) : '';
      const toStr = hasTo ? String(usageTo) : '';
      return fromStr || toStr || null;
    };
    
    // Helper to format load factor range, only showing if meaningful values exist
    const formatLoadFactorRange = () => {
      const hasFrom = loadFactorFrom !== null && loadFactorFrom !== undefined;
      const hasTo = loadFactorTo !== null && loadFactorTo !== undefined;
      
      if (!hasFrom && !hasTo) {
        return null;
      }
      
      // Build range string - show "From - To" format
      if (hasFrom && hasTo) {
        return `${loadFactorFrom} - ${loadFactorTo}`;
      }
      
      // If only one is provided, still show it (validation will handle requiring both)
      const fromStr = hasFrom ? String(loadFactorFrom) : '';
      const toStr = hasTo ? String(loadFactorTo) : '';
      return fromStr || toStr || null;
    };
    
    const rateClassLabel = pricingState.selectedState?.name === 'Texas' ? 'Load Profile' : 'Rate Class';

    const mapping: Record<string, Array<[string, string | null]>> = {
      Market: [["Market", selectedFilters["Market"]]],
      Geography: [
        ["State", selectedFilters["State"]],
        ["Utility", selectedFilters["Utility"]],
        ...(!isGasMarket ? [["Load Zone", selectedFilters["Load Zone"]] as [string, string | null]] : []),
      ],
      LoadCharacteristics: [
        [rateClassLabel, selectedFilters["Rate Class"]],
        // ...(
        //   // true
        //   Array.isArray(adminClients) && adminClients.length > 0 
        // ? [["Pass Through", selectedFilters["Pass Through"]] as [string, string | null]] : []),
        ["Annual Usage", formatUsageRange()],
        // ["Demand", (demand !== undefined && demand !== null) ? String(demand) : null],
        ["Load Factor (%)", formatLoadFactorRange()],
      ],
      PricingParameters: [
        ["Term", selectedFilters["Term"]],
        ["Booked Date", selectedFilters["Effective Date"]],
        ["Start Date", selectedFilters["Start Date"]],
        // ["No Of Month", selectedFilters["No Of Month"]],
        ["Product Type", selectedFilters["ProductType"]],
        ["Billing Method", selectedFilters["Billing Method"]],
        ["Customer Class", selectedFilters["Customer Class"]],
      ],
      // Product: [
      //   ["Product Type", selectedFilters["ProductType"]],
      // ],
    };
    return mapping[sectionKey] || [];
  };

  // const renderBadges = (sectionKey: string) => (
  //   <span className="ms-2 d-inline-flex flex-wrap align-items-center gap-1">
  //     {getBadgesForSection(sectionKey).map(([label, value]) => (
  //       value && value !== 'All' && value !== '-' ? (
  //         <Badge key={`${sectionKey}-${label}`} className="mx-1" bg="secondary">
  //           {label}: {value}
  //           <i
  //             className="mdi mdi-close ms-1"
  //             onClick={(e) => handleDeleteBadge(label, e)}
  //             style={{ cursor: 'pointer' }}
  //             role="button"
  //             aria-label={`Remove ${label}`}
  //           />
  //         </Badge>
  //       ) : null
  //     ))}
  //   </span>
  // );
const renderAllBadges = () => {
    const sections = [
      "Market",
      "Geography",
      "LoadCharacteristics",
      "PricingParameters",
      "Product",
    ];
    const allBadges = sections.flatMap((sectionKey) =>
      getBadgesForSection(sectionKey)
    );
    const activeBadges = allBadges.filter(
      ([label, value]) => {
        // For Pass Through and Customer Class, show badge even when value is "All"
        if (label === "Pass Through" || label === "Customer Class") {
          return value && value !== "";
        }
        // For Market, Start Date, and Booked Date, always show badge (like Market badge behavior)
        if (label === "Market" || label === "Start Date" || label === "Booked Date") {
          return value && value !== "";
        }
        // For Rate Class and Load Profile, exclude "All" and "None" values
        if (label === "Rate Class" || label === "Load Profile") {
          return value && value !== "All" && value !== "None" && value !== "";
        }
        // For all other fields, exclude "All" values
        return value && value !== "All" && value !== "";
      }
    );
    if (activeBadges.length === 0) return null;
    return (
      <Div className="d-flex flex-wrap align-items-center gap-1">
        {activeBadges.map(([label, value]) => (
          <Badge key={`all-${label}`} className="mx-1" bg="secondary">
            {label}: {value}
            <CloseIcon
              className="ms-1"
              onClick={(e) => handleDeleteBadge(label, e)}
              style={{ cursor: "pointer" }}
              role="button"
              aria-label={`Remove ${label}`}
            />
          </Badge>
        ))}
      </Div>
    );
  };
  const handleDeleteBadge = (label: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (typeof onDeleteFilter === 'function') onDeleteFilter(label);
  };

  const handleMarketSelect = (market: any) => {
    dispatch(setSelectedMarket({
      value: market.value,
      name: market.name,
      commodity: market.commodity
    }));
  };

  const getCommodityIcon = (commodity: string) => {
    return commodity === 'NatGas'
      ? <GasIcon className="text-success me-1" size="sm" />
      : <ElectricityIcon className="text-warning me-1" size="sm" />;
  };

  // Select first market by default when markets load and none selected
  useEffect(() => {
    if (!selectedMarket && markets && markets.length > 0) {
      // Try to find ERCOT market by name or value (case-insensitive)
      const ercotMarket = markets.find((m: any) => {
        const name = (m.name || m.value || '').toString().toUpperCase();
        return name === 'ERCOT';
      });
      const marketToSelect = ercotMarket || markets[0];
      const commodity = (marketToSelect as any)?.commodity ?? '';
      dispatch(setSelectedMarket({
        value: marketToSelect.value,
        name: marketToSelect.name,
        commodity
      }));
    }
  }, [markets, selectedMarket, dispatch]);

  return (
    <>
     <Form onSubmit={handleSubmit} className="ranking-filters">
       {/* Market */}
      <Div className="mb-2 mt-0 ranking-section">
                <Div className="row g-3 align-items-start align-items-lg-center">
        {/* <Div className="row g-3 align-items-start align-items-xl-end"> */}
          <Div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
            <h5>Market</h5>
          </Div>
          <Div className={Array.isArray(adminClients) && adminClients.length > 0 ? "col-lg" : "col"}>
            <Div className="d-flex flex-wrap gap-1">
              {markets.length === 0 ? (
                <div className="text-muted">No markets available</div>
              ) : (
                markets.map((market: any) => (
                  <Button
                    key={market.id}
                    type="button"
                    variant={selectedMarket?.value === market.value ? "primary" : "outline-secondary"}
                    size="sm"
                   className="rounded-1 d-flex align-items-center gap-1 py-0 px-2"
                    disabled={filterActionsDisabled}
                    onClick={() => handleMarketSelect(market)}
                  >
                    {getCommodityIcon(market.commodity)}
                    <span className="small">{market.name}</span>
                  </Button>
                ))
              )}
            </Div>
          </Div>
          {Array.isArray(adminClients) && adminClients.length > 0 && (
            <Div className="col-12 col-lg-auto">
              <Div className="market-viewas-panel">
                <Div className="market-viewas-top-row">
                  {
                    false && <><Div className="section-heading-column d-flex align-items-center market-viewas-heading">
                    <h5>View As</h5>
                  </Div>
                  <Div className="market-viewas-select">
                    <FormInput
                      type="select"
                      label=""
                      name="clientOverride"
                      control={control}
                      options={adminClients}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      clearable={false}
                      isLoading={dropdownLoadingState.adminClients}
                      maxMenuHeight={245}
                      containerClass="mb-0"
                    />
                  </Div></>
                  }
                  
                  <Div className="market-viewas-checkbox-row">
                    <FormInput
                      type="checkbox"
                      name="showAll"
                      label="Disable Anonymization"
                      control={control}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      inputClassName="custom-checkbox-size market-viewas-checkbox"
                      containerClass="mb-0"
                    />
                  </Div>
                </Div>
              </Div>
            </Div>
          )}
        </Div>
      </Div>
 
      {/* Geography */}
      <Div className="mb-2 geography-section ranking-section">
        {/* <Div className="row g-3 align-items-end"> */}
        {/* <Div className="row g-3 align-items-start align-items-xl-end"> */}
        <Div className="row g-3 align-items-start align-items-lg-center">
          <Div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
            <h5>Geography</h5>
          </Div>
          <Div className="col">
            {/* <Div className="row  align-items-end"> */}
            <Div className="row  align-items-start align-items-xl-end">
              {isGasMarket ? (
                <>
                  <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                    <FormInput
                      type="select"
                      label="State"
                      name="stateCode"
                      control={control}
                      options={dependantDropdowns.states || []}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      isLoading={dropdownLoadingState.states}
                      maxMenuHeight={245}
                    />
                  </Div>
                  <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                    <FormInput
                      type="select"
                      label="Utility"
                      name="utility"
                      control={control}
                      options={dependantDropdowns.utilities || []}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      isLoading={dropdownLoadingState.utilities}
                      maxMenuHeight={245}
                    />
                  </Div>
                </>
              ) : (
                <>
                  <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                    <FormInput
                      type="select"
                      label="State"
                      name="stateCode"
                      control={control}
                      options={dependantDropdowns.states || []}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      isLoading={dropdownLoadingState.states}
                      maxMenuHeight={245}
                    />
                  </Div>
                  <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                    <FormInput
                      type="select"
                      label="Utility"
                      name="utility"
                      control={control}
                      options={dependantDropdowns.utilities || []}
                      errors={errors}
                      labelClassName="form-label geography-label"
                      isLoading={dropdownLoadingState.utilities}
                      maxMenuHeight={245}
                    />
                  </Div>
                </>
              )}
              {!isGasMarket && (
                <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                  <FormInput
                    type="select"
                    label="Load Zone"
                    name="loadZone"
                    control={control}
                    options={dependantDropdowns.loadZones || []}
                    errors={errors}
                    labelClassName="form-label geography-label"
                    isLoading={dropdownLoadingState.loadZones}
                    maxMenuHeight={245}
                  />
                </Div>
              )}
            </Div>
          </Div>
        </Div>
      </Div>
 
      {/* Load Characteristics */}
      <Div className="mb-2 load-characteristics-section ranking-section">
        {/* <Div className="row g-3 align-items-start align-items-xxl-end"> */}
        <Div className="row g-3 align-items-start align-items-lg-center">

          <Div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
            <h5>Load Characteristics</h5>
          </Div>
          <Div className="col">
            {/* <Div className="row align-items-start align-items-end"> */}
            <Div className="row  align-items-start align-items-xl-end ">
              <Div className="col-12 col-lg-4 col-xl-4">
                {
                pricingState.selectedState?.name === 'Texas' ? (
                  <FormInput
                    type="select"
                    label="Load Profile"
                    name="loadProfile"
                    control={control}
                    options={dependantDropdowns.loadProfiles || []}
                    errors={errors}
                    labelClassName="form-label geography-label"
                    isLoading={dropdownLoadingState.loadProfiles}
                    maxMenuHeight={245}
                  />
                ) : (
                  <FormInput
                    type="select"
                    label="Rate Class"
                    name="rateClass"
                    control={control}
                    options={dependantDropdowns.rateClasses || []}
                    errors={errors}
                    labelClassName="form-label geography-label"
                    isLoading={dropdownLoadingState.rateClasses}
                    maxMenuHeight={245}
                  />
                )}
              </Div>
              <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="select"
                  label="Customer Class"
                  name="customerType"
                  control={control}
                  options={dependantDropdowns.customerTypes || []}
                  errors={errors}
                  labelClassName="form-label geography-label text-nowrap"
                  isLoading={dropdownLoadingState.customerTypes}
                  maxMenuHeight={245}
                />
              </Div>

              {/* <Div className="col-12 col-sm-6 col-lg-1 col-xl-1">
                <FormInput
                  type="number"
                  label="Demand"
                  name="demand"
                  control={control}
                  register={register}
                  errors={errors}
                  placeholder=""
                  labelClassName="form-label geography-label"
                  inputClassName="w-100 fw-semibold ranking-input-strong"
                  min={undefined}
                  max={100000}
                  step={1}
                  // validation={{
                  //   min: { value: 0, message: "Demand must be 0 or greater" },
                  //   max: { value: 100000, message: "Maximum demand is 100,000" },
                  //   setValueAs: (v: any) => {
                  //     if (v === "" || v === null || v === undefined) return null;
                  //     const num = Number(v);
                  //     if (Number.isFinite(num) && num >= 0) return num;
                  //     return null;
                  //   },
                  // }}
                />
              </Div> */}

              <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                <Div className="d-flex flex-column">
                  <Form.Label className="form-label geography-label text-center">Load Factor (%)</Form.Label>
                  <Div className="d-flex align-items-end gap-2">
                    <Div className="flex-grow-1 d-flex flex-column">
                      <Form.Label className="form-label geography-label text-center">From</Form.Label>
                      <FormInput
                        type="number"
                        name="loadFactorFrom"
                        control={control}
                        register={register}
                        errors={errors}
                        placeholder=""
                        labelClassName="d-none"
                        inputClassName="w-100 fw-semibold ranking-input-strong no-spinner"
                        min={undefined}
                        max={2000000}
                        step={1}
                        tooltip={errors?.loadFactorFrom?.message as string}
                        tooltipPlacement="top"
                        validation={{
                          validate: (value: any) => {
                            const hasValue = value !== null && value !== undefined && value !== "";
                            const hasTo = watchLoadFactorTo !== null && watchLoadFactorTo !== undefined && watchLoadFactorTo !== "";
                            
                            // If Load Factor To is provided, Load Factor From is required
                            if (hasTo && !hasValue) {
                              return "Load Factor From is required";
                            }
                            
                            if (!hasValue) {
                              return true; // Allow empty value if Load Factor To is also empty
                            }
                            
                            const numValue = Number(value);
                            if (!Number.isFinite(numValue)) {
                              return true; // Let other validations handle invalid numbers
                            }
                            
                            if (hasTo) {
                              const numTo = Number(watchLoadFactorTo);
                              if (Number.isFinite(numTo) && numValue >= numTo) {
                                return "Load Factor From must be less than Load Factor To";
                              }
                            }
                            
                            return true;
                          },
                          setValueAs: (v: any) => {
                            if (v === "" || v === null || v === undefined) return null;
                            const num = Number(v);
                            if (Number.isFinite(num) && num >= 0) return num;
                            return null;
                          },
                        }}
                      />
                    </Div>
                    <span className="fw-semibold mb-2">-</span>
                    <Div className="flex-grow-1 d-flex flex-column">
                      <Form.Label className="form-label geography-label text-center">To</Form.Label>
                      <FormInput
                        type="number"
                        name="loadFactorTo"
                        control={control}
                        register={register}
                        errors={errors}
                        placeholder=""
                        labelClassName="d-none"
                        inputClassName="w-100 fw-semibold ranking-input-strong no-spinner"
                        min={undefined}
                        max={2000000}
                        step={1}
                        tooltip={errors?.loadFactorTo?.message as string}
                        tooltipPlacement="top"
                        validation={{
                          validate: (value: any) => {
                            const hasValue = value !== null && value !== undefined && value !== "";
                            const hasFrom = watchLoadFactorFrom !== null && watchLoadFactorFrom !== undefined && watchLoadFactorFrom !== "";
                            
                            // If Load Factor From is provided, Load Factor To is required
                            if (hasFrom && !hasValue) {
                              return "Load Factor To is required";
                            }
                            
                            if (!hasValue) {
                              return true; // Allow empty value if Load Factor From is also empty
                            }
                            
                            const numValue = Number(value);
                            if (!Number.isFinite(numValue)) {
                              return true; // Let other validations handle invalid numbers
                            }
                            
                            if (hasFrom) {
                              const numFrom = Number(watchLoadFactorFrom);
                              if (Number.isFinite(numFrom) && numValue <= numFrom) {
                                return "Load Factor To must be greater than Load Factor From";
                              }
                            }
                            
                            return true;
                          },
                          setValueAs: (v: any) => {
                            if (v === "" || v === null || v === undefined) return null;
                            const num = Number(v);
                            if (Number.isFinite(num) && num >= 0) return num;
                            return null;
                          },
                        }}
                      />
                    </Div>
                  </Div>
                </Div>
              </Div>

              <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                <Div className="d-flex flex-column">
                  <Form.Label className="form-label geography-label text-center">Annual {usageLabel}</Form.Label>
                  <Div className="d-flex align-items-end gap-2">
                    <Div className="flex-grow-1 d-flex flex-column">
                      <Form.Label className="form-label geography-label text-center">From</Form.Label>
                      <FormInput
                        type="number"
                        name="usageFrom"
                        control={control}
                        register={register}
                        errors={errors}
                        placeholder=""
                        labelClassName="d-none"
                        inputClassName="w-100 fw-semibold ranking-input-strong no-spinner text-nowrap"
                        min={undefined}
                        max={2000000}
                        step={1}
                        tooltip={errors?.usageFrom?.message as string}
                        tooltipPlacement="top"
                        validation={{
                          validate: (value: any) => {
                            const hasValue = value !== null && value !== undefined && value !== "";
                            const hasTo = watchUsageTo !== null && watchUsageTo !== undefined && watchUsageTo !== "";
                            
                            // If Usage To is provided, Usage From is required
                            if (hasTo && !hasValue) {
                              return "Usage From is required";
                            }
                            
                            if (!hasValue) {
                              return true; // Allow empty value if Usage To is also empty
                            }
                            
                            const numValue = Number(value);
                            if (!Number.isFinite(numValue)) {
                              return true; // Let other validations handle invalid numbers
                            }
                            
                            if (hasTo) {
                              const numTo = Number(watchUsageTo);
                              if (Number.isFinite(numTo) && numValue >= numTo) {
                                return "Usage From must be less than Usage To";
                              }
                            }
                            
                            return true;
                          },
                          setValueAs: (v: any) => {
                            if (v === "" || v === null || v === undefined) return null;
                            const num = Number(v);
                            if (Number.isFinite(num) && num >= 0) return num;
                            return null;
                          },
                        }}
                      />
                    </Div>
                    <span className="fw-semibold mb-2">-</span>
                    <Div className="flex-grow-1 d-flex flex-column">
                      <Form.Label className="form-label geography-label text-center">To</Form.Label>
                      <FormInput
                        type="number"
                        name="usageTo"
                        control={control}
                        register={register}
                        errors={errors}
                        placeholder=""
                        labelClassName="d-none"
                        inputClassName="w-100 fw-semibold ranking-input-strong no-spinner"
                        min={undefined}
                        max={2000000}
                        step={1}
                        tooltip={errors?.usageTo?.message as string}
                        tooltipPlacement="top"
                        validation={{
                          validate: (value: any) => {
                            const hasValue = value !== null && value !== undefined && value !== "";
                            const hasFrom = watchUsageFrom !== null && watchUsageFrom !== undefined && watchUsageFrom !== "";
                            
                            // If Usage From is provided, Usage To is required
                            if (hasFrom && !hasValue) {
                              return "Usage To is required";
                            }
                            
                            if (!hasValue) {
                              return true; // Allow empty value if Usage From is also empty
                            }
                            
                            const numValue = Number(value);
                            if (!Number.isFinite(numValue)) {
                              return true; // Let other validations handle invalid numbers
                            }
                            
                            if (hasFrom) {
                              const numFrom = Number(watchUsageFrom);
                              if (Number.isFinite(numFrom) && numValue <= numFrom) {
                                return "Usage To must be greater than Usage From";
                              }
                            }
                            
                            return true;
                          },
                          setValueAs: (v: any) => {
                            if (v === "" || v === null || v === undefined) return null;
                            const num = Number(v);
                            if (Number.isFinite(num) && num >= 0) return num;
                            return null;
                          },
                        }}
                      />
                    </Div>
                  </Div>
                </Div>
              </Div>
{/* {
Array.isArray(adminClients) && adminClients.length > 0 
// true
&& (
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                <FormInput
                  type="select"
                  label="Pass Through"
                  name="passThrough"
                  control={control}
                  options={passThroughOptions}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  multiple
                  placeholder="Select"
                  containerClass="mb-0 w-100"
                  isLoading={dropdownLoadingState.costComponents}
                />
              </Div>
               )} */}
              {/* 2/12: Usage From */}
              {/* <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                 <FormInput
                type="number"
                label="Usage:"
                name="usage"
                control={control}
                register={register}
                errors={errors}
                placeholder="Enter usage"
                inputClassName="w-100"
                labelClassName="form-label geography-label"
                min={undefined}
                max={2000000}
                step={1}
                validation={{ 
                  min: { value: 0, message: 'Usage must be 0 or greater' },
                  max: { value: 2000000, message: 'Maximum usage is 2,000,000' },
                  setValueAs: (v: any) => {
                    // Handle empty/null/undefined as null (not 0)
                    if (v === '' || v === null || v === undefined) return null;
                    const num = Number(v);
                    // Only return valid positive numbers or 0 if explicitly entered
                    if (Number.isFinite(num) && num >= 0) return num;
                    return null;
                  }
                }}
              />
              </Div> */}
              {/* 2/12: Usage To */}
              {/* <Div className="col-6 col-md-2">

                <FormInput
                  type="number"
                  label="Usage To:"
                  name="usageTo"
                  control={control}
                  register={register}
                  errors={errors}
                  placeholder="Enter usage to"
                  containerClass={'mb-0'}
                  labelClassName="mb-0"
                  inputClassName="w-100"
                  inline={true}
                  min={undefined}
                  max={2000000}
                  step={1}
                  validation={{ 
                    min: { value: 0, message: 'Usage To must be 0 or greater' },
                    max: { value: 2000000, message: 'Maximum usage is 2,000,000' },
                    validate: {
                      greaterThanFrom: (value: any) => {
                        // Only validate if both fields have values
                        if (value !== null && value !== undefined && watchUsageFrom !== null && watchUsageFrom !== undefined) {
                          return Number(value) >= Number(watchUsageFrom) || 'Usage To must be greater than or equal to Usage From';
                        }
                        return true;
                      }
                    },
                    setValueAs: (v: any) => {
                      // Handle empty/null/undefined as null (not 0)
                      if (v === '' || v === null || v === undefined) return null;
                      const num = Number(v);
                      // Only return valid positive numbers or 0 if explicitly entered
                      if (Number.isFinite(num) && num >= 0) return num;
                      return null;
                    }
                  }}
                />
              </Div> */}
              {/* 2/12: Demand */}
              {/* <Div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                <FormInput
                  type="number"
                  label="Demand:"
                  name="demand"
                  control={control}
                  register={register}
                  errors={errors}
                  placeholder="Enter demand"
                  inputClassName="w-100"
                  labelClassName="form-label geography-label"
                  min={undefined}
                  max={100000}
                  step={1}
                  validation={{ 
                    min: { value: 0, message: 'Demand must be 0 or greater' },
                    max: { value: 100000, message: 'Maximum demand is 100,000' },
                    setValueAs: (v: any) => {
                      // Handle empty/null/undefined as null (not 0)
                      if (v === '' || v === null || v === undefined) return null;
                      const num = Number(v);
                      // Only return valid positive numbers or 0 if explicitly entered
                      if (Number.isFinite(num) && num >= 0) return num;
                      return null;
                    }
                  }}
                />
              </Div> */}
            
               {/* {Array.isArray(adminClients) && adminClients.length > 0 && (
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                <FormInput
                  type="select"
                  label="Pass Through"
                  name="passThrough"
                  control={control}
                  options={filteredCostComponents}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  multiple
                  placeholder="Select"
                  containerClass="mb-0 w-100"
                  isLoading={dropdownLoadingState.costComponents}
                />
              </Div>
               )} */}
            </Div>
          </Div>
        </Div>
      </Div>
 
      {/* Pricing Parameters */}
      <Div className="mb-2 pricing-parameters-section ranking-section">
        {/* <Div className="row g-3 align-items-start align-items-xl-end"> */}
                <Div className="row g-3 align-items-start align-items-lg-center">

          <Div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
            <h5>Pricing Parameters</h5>
          </Div>
          <Div className="col">
            <Div className="row  align-items-start align-items-xl-end">
              <Div className="col-12 col-sm-6 col-lg-1 col-xl-1">
                <FormInput
                  type="number"
                  label="Term"
                  name="term"
                  control={control}
                  register={register}
                  errors={errors}
                  placeholder=""
                  labelClassName="form-label geography-label"
                  inputClassName="w-100 no-spinner"
                  min={1}
                  max={240}
                  step={1}
                  // validation={{
                  //   min: { value: 1, message: 'Term must be 1 or greater' },
                  //   max: { value: 240, message: 'Maximum term is 240' },
                  //   setValueAs: (v: any) => {
                  //     if (v === '' || v === null || v === undefined) return null;
                  //     const num = Number(v);
                  //     if (Number.isFinite(num) && num >= 1 && num <= 240) {
                  //       return Math.floor(num); // Ensure integer
                  //     }
                  //     return null;
                  //   }
                  // }}
                />
              </Div>
              <Div className="col-12 col-sm-6 col-lg-2 col-xl-2">
                <FormInput
                  type="date"
                  label="Start Date"
                  name="startToDate"
                  key={`start-date-${resetKey}`}
                  control={control}
                  errors={errors}
                  placeholder="mm/yy"
                  labelClassName="form-label geography-label"
                  onlyMonthPicker
                  highlightToday={false}
                  minDate={new Date(new Date().getFullYear() - 3, 0, 1)}
                  // maxDate={new Date(new Date().getFullYear() + 2, 11, 31)}
                  readOnly={true}
                />
              </Div>
              
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                <FormInput
                  type="date"
                  label="Booked Date"
                  name="effectiveDate"
                  key={`effective-${resetKey}`}
                  control={control}
                  errors={errors}
                  placeholder="mm/dd/yy"
                  labelClassName="form-label geography-label"
                  range
                  // minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 1))}
                  // maxDate={new Date()}
                  readOnly={true}
                />
              </Div>
              {/* <Div className="col-12 col-sm-6 col-lg-1 col-xl-1">
                <FormInput
                  type="select"
                  label="Forward Months"
                  name="noOfMonth"
                  control={control}
                  options={NO_OF_MONTH_OPTIONS}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  placeholder="Select"
                />
              </Div> */}
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                <FormInput
                  type="select"
                  label="Billing Method"
                  name="billingMethod"
                  control={control}
                  options={dependantDropdowns.billingMethods || []}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  isLoading={dropdownLoadingState.billingMethods}
                  maxMenuHeight={245}
                />
              </Div>
              <Div className="col-12 col-sm-6 col-lg-3 col-xl-3">
                <FormInput
                  type="select"
                  label="Product Type"
                  name="productType"
                  control={control}
                  options={productTypeList}
                  errors={errors}
                  labelClassName="form-label geography-label"
                  multiple
                  placeholder="All"
                  maxMenuHeight={245}
                />
              </Div>
               
            </Div>
          </Div>
        </Div>
      </Div>
 
      {/* <Div className="col-12 col-md-6">
        <Div className="mb-2">
        <Div className="d-flex alignments align-items-center justify-content-between mb-1">
          <h5 className="mb-0">Product</h5>
          {renderBadges('Product')}
        </Div> */}
        {/* <Div className="d-flex alignments align-items-center gap-2 flex-wrap"> */}
          {/* <Div style={{ minWidth: "300px", flex: "0 0 350px" }}> */}
          {/* <FormInput
            type="select"
            label="Product Type:"
            name="productType"
            control={control}
            options={productTypeList}
            errors={errors}
            containerClass={'flex-grow-1'}
            labelClassName="mb-0"
            inline={true}
            
             
          />
          </Div>
        </Div>
      </Div> */}
      {/* <Div className="mt-2 mb-1">{renderAllBadges()}</Div> */}

      {/* <Div className="mb-2 pricing-parameters-section ranking-section">
        <Div className="row g-3 alignments-end">
          <Div className="col-12 col-md-auto section-heading-column d-flex alignments-center">
            <h5>View As</h5>
          </Div>
          {Array.isArray(adminClients) && adminClients.length > 0 ? (
            <Div className="col-12 col-md-3">
              <FormInput
                type="select"
                label=""
                name="clientOverride"
                control={control}
                options={adminClients}
                errors={errors}
                labelClassName="form-label geography-label"
                clearable={false}
              />
            </Div>
          ) : (
            <Div className="col"><span /></Div>
          )}
        </Div>
      </Div> */}

<Div className="mt-2 mb-1">{renderAllBadges()}</Div>

      {/* Save Filter Name Modal */}
      <ConfirmationModal
        dialogClassName="modal-md"
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        centered
        size="sm"
        title="Save Filter"
        confirmButtonLabel={savingFilters ? "Saving..." : "Save & Apply"}
        isLoading={savingFilters}
        onConfirm={() => {
          if (filterName.trim()) {
            onSaveFilters?.(filterName.trim());
            setShowSaveModal(false);
            setFilterName("");
          }
        }}
      >
          <FormInput
            type="text"
            name="filterName"
            placeholder="Enter filter name"
            value={filterName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterName(e.target.value)}
            autoFocus
          />
      </ConfirmationModal>
    </Form>
    </>
  );
}

export default AccordionFilters;
